import React from "react";
import { DrugCostInfo, Plan, PrescriptionDrug } from "../../../@types";
import { GlobalSelectFilter, RenderedTable } from "../../../components";
import {
  $$,
  useTranslate,
  displayDrug,
  useTableConfig,
  shouldSuppressDrugPricing,
  planHasDrugCoverage,
  useBelowLargeBreakpoint,
  getCellStringValueFromHeaderProps,
} from "../../../helpers";
import { AppContext } from "../../../app/store";
import { hasMailOrderPharmacy } from "../../../app/store/AppStore";
import { PharmacyNetworkStatusStackedHeading } from "../components";
import {
  AugmentedColumn,
  AugmentedHeaderProps,
  col,
} from "../../../@types/react-table";
import { CellValue, TableInstance } from "react-table";
import { linkToPartDCoPaymentCoInsurance } from "../../../helpers/CONSTANTS";

export const YearlyDrugCostsByPharmacyTable: React.FunctionComponent<{
  drugCostInfo: DrugCostInfo;
  plan: Plan;
}> = ({ drugCostInfo, plan }) => {
  const t = useTranslate();
  const { state } = React.useContext(AppContext);
  const { language } = state;
  const includesMailOrder = hasMailOrderPharmacy(state);
  const nestedCols = [{ accessor: "ndc" }] as AugmentedColumn[];
  const suppressDrugPricing = shouldSuppressDrugPricing(plan);

  const optionalContentProps = {};
  useBelowLargeBreakpoint(matches => {
    optionalContentProps["aria-hidden"] = matches;
  }, []);

  // key = ndc, value = drug cost for each pharmacy
  const costs = new Map();
  const totalCosts = {
    ndc: t("plan_details.drug_coverage.total_yearly_drug_costs"),
  };
  drugCostInfo.costs.forEach(pharmacy => {
    if (pharmacy.npi || includesMailOrder) {
      const pharmacyKey = pharmacy.npi || "mail-order";
      nestedCols.push({
        accessor: pharmacyKey,
        Header: (props: AugmentedHeaderProps<col>) => (
          <PharmacyNetworkStatusStackedHeading
            drugCostInfo={drugCostInfo}
            includeName
            isTableHeader
            npi={pharmacy.npi}
            hasDrugCoverage={planHasDrugCoverage(plan)}
            // Add costs to stacked view, sr-only content
            stackedViewScreenReaderContent={getCellStringValueFromHeaderProps(
              props
            )}
          />
        ),
        Cell: function cellRenderer({
          value,
        }: Partial<TableInstance> | CellValue) {
          return (
            <div
              className="e2e-yearly-drug-costs-by-pharmacy-drug-cost"
              // `aria-hide` costs (cell value) below large breakpoint
              // they are in sr-only output of the pharamacy network status component
              {...optionalContentProps}
            >
              {value}
            </div>
          );
        },
      });
      totalCosts[pharmacyKey] = suppressDrugPricing
        ? t("na")
        : $$(pharmacy.estimated_yearly_total);
      pharmacy.drug_costs.forEach(drug => {
        // find selected drug from state
        const prescriptionDrug =
          state.prescriptions.find(p => p.ndc === drug.ndc) ||
          ({} as PrescriptionDrug);

        const drugCost = costs.get(drug.ndc) || {
          ndc: displayDrug(t, prescriptionDrug),
        };
        drugCost[pharmacyKey] = (
          <div className="ds-u-margin-top--1 ds-u-lg-margin-top--0">
            {suppressDrugPricing ? t("na") : $$(drug.estimated_yearly_total)}
          </div>
        );
        costs.set(drug.ndc, drugCost);
      });
    }
  });
  const rowData: { ndc: string | JSX.Element }[] = Array.from(costs.values());
  rowData.push(totalCosts);

  const columns: AugmentedColumn[] = useTableConfig(
    [
      {
        accessor: "filter_col1",
        Header: null,
        Filter: GlobalSelectFilter,
        filter: "includes",
        defaultCanFilter: true,
        columns: nestedCols,
        className: "e2e-yearly-drug-costs-by-pharmacy-filter-header",
      },
    ],
    [language, drugCostInfo]
  );
  const data = useTableConfig(rowData, [drugCostInfo, language]);

  return (
    <RenderedTable
      caption={{
        title: t("plan_details.drug_coverage.yearly_drug_costs_by_pharmacy"),
        render: function renderTableCaption(title: JSX.Element) {
          return (
            <>
              {title}
              <p className="ds-u-margin-y--1">
                {t(
                  "plan_details.drug_coverage.yearly_drug_costs_by_pharmacy.description"
                )}{" "}
                <a
                  target="_blank"
                  href={linkToPartDCoPaymentCoInsurance(state.language)}
                  rel="noopener noreferrer"
                >
                  {t(
                    "plan_details.drug_coverage.yearly_drug_costs_by_pharmacy.description.question_link"
                  )}
                </a>
              </p>
            </>
          );
        },
      }}
      className={`e2e-yearly-drug-costs-by-pharmacy pharmacy-column-table__${drugCostInfo
        .costs.length || 1}`}
      rowHeaders
      borderless
      stackableBreakpoint="lg"
      tableColumns={columns}
      tableData={data}
      autoResetGlobalFilter={false}
      state={{
        filterConfig: {
          ariaLabel: t(
            "plan_details.drug_coverage.yearly_drug_costs_by_pharmacy.filter.aria_label"
          ),
          label: t(
            "plan_details.drug_coverage.yearly_drug_costs_by_pharmacy.filter_label"
          ),
          buttonLabel: t(
            "plan_details.drug_coverage.yearly_drug_costs_by_pharmacy.change_filter"
          ),
          hideFilteredRowHeading: true,
        },
      }}
    />
  );
};
